/**
 * Компонент списка сообщений
 */

import { useEffect, useRef, useCallback } from 'react';
import styles from './MessagesList.module.css';
import { Message } from '@/types/message.types';
import { ChatMessage, TypingIndicator } from '../ChatMessage';
import { useConfigStore } from '@/store/configStore';

interface MessagesListProps {
  messages: Message[];
  isTyping?: boolean;
  onSendMessage?: (apiText: string, displayText?: string) => void;
}

const SCROLL_THRESHOLD = 150;

export function MessagesList({ messages, isTyping = false, onSendMessage }: MessagesListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isNearBottom = useRef(true);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    isNearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  }, []);

  const translations = useConfigStore((s) => s.translations);

  useEffect(() => {
    if (isNearBottom.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  if (messages.length === 0 && !isTyping) {
    return (
      <div className={`${styles.messagesList} ${styles.empty}`} role="log" aria-live="polite">
        <p>{translations.empty_chat || 'Start a conversation'}</p>
      </div>
    );
  }

  return (
    <div ref={listRef} className={styles.messagesList} role="log" aria-live="polite" onScroll={handleScroll}>
      {messages.map((message) => (
        <ChatMessage key={message.id} message={message} onSendMessage={onSendMessage} />
      ))}

      {isTyping && (
        <div style={{ alignSelf: 'flex-start' }}>
          <TypingIndicator />
        </div>
      )}

      {/* Элемент для auto-scroll */}
      <div ref={messagesEndRef} />
    </div>
  );
}

